/**
 * React Flow canvas and selection state for the protocol DAG editor.
 */

import {
  Background,
  Controls,
  MiniMap,
  ReactFlow,
  useReactFlow,
  type NodeTypes,
} from "@xyflow/react";
import { useCallback, useEffect, useState } from "react";
import {
  ProtocolDagInspector,
  type PendingConnection,
} from "./ProtocolDagInspector";
import { ProtocolDagNode } from "./ProtocolDagNodes";
import { ProtocolDagShell } from "./ProtocolDagShell";
import type { ProtocolNodeKind } from "./protocolDagConnectionRules";
import type {
  ProtocolDagEdgeData,
  ProtocolDagNodeData,
} from "./protocolDagModel";
import { PROTOCOL_TIERS } from "./ProtocolTierFilter";
import { filterProtocolGraphByTiers } from "./protocolDagFilter";
import type { ProtocolDagCanvasProps } from "./protocolDagTypes";
import { useProtocolDagConnect } from "./useProtocolDagConnect";
import { useProtocolDagGraph } from "./useProtocolDagGraph";

const nodeTypes: NodeTypes = { protocolNode: ProtocolDagNode };

export type { ProtocolDagCanvasProps } from "./protocolDagTypes";

/**
 * Own React Flow state for one draft graph and the side inspector.
 *
 * @param props - Graph entities and mutation handlers
 * @returns Canvas + inspector layout
 */
export function ProtocolDagCanvas({
  criteria,
  atoms,
  gates,
  connections,
  selectedTiers = [...PROTOCOL_TIERS],
  focusCriterionId = null,
  onAddConnection,
  onRemoveConnection,
  readOnly = false,
  busy = false,
}: ProtocolDagCanvasProps) {
  const { fitView } = useReactFlow();
  const filtered = filterProtocolGraphByTiers(
    criteria,
    atoms,
    gates,
    connections,
    selectedTiers,
  );
  const {
    nodes,
    edges,
    onNodesChange,
    onEdgesChange,
    summary,
    expandedCriterionId,
    setExpandedCriterionId,
    toggleCriterion,
  } = useProtocolDagGraph(
    filtered.criteria,
    filtered.atoms,
    filtered.gates,
    filtered.connections,
    focusCriterionId,
  );

  const [isGraphExpanded, setIsGraphExpanded] = useState(false);
  const [selectedNode, setSelectedNode] = useState<ProtocolDagNodeData | null>(
    null,
  );
  const [selectedEdge, setSelectedEdge] = useState<ProtocolDagEdgeData | null>(
    null,
  );
  const [pendingConnection, setPendingConnection] =
    useState<PendingConnection | null>(null);

  // Only re-fit when the shell size changes (fullscreen toggle). Do not depend
  // on fitView — its identity can change every render and would re-center on
  // ordinary node selection clicks.
  useEffect(() => {
    const timer = window.setTimeout(() => {
      window.dispatchEvent(new Event("resize"));
      void fitView({ padding: 0.18, duration: 200 });
    }, 100);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fitView is unstable
  }, [isGraphExpanded]);

  const resolveKind = useCallback(
    (nodeId: string): ProtocolNodeKind | null =>
      nodes.find((item) => item.id === nodeId)?.data.kind ?? null,
    [nodes],
  );

  const clearSelection = useCallback(() => {
    setSelectedNode(null);
    setSelectedEdge(null);
  }, []);

  const { commitConnection, onConnect } = useProtocolDagConnect({
    resolveKind,
    onAddConnection,
    readOnly,
    setPendingConnection,
    clearSelection,
  });

  const handleToggleCriterion = useCallback(
    (criterionId: string) => {
      const willExpand = expandedCriterionId !== criterionId;
      toggleCriterion(criterionId);
      if (willExpand) setIsGraphExpanded(true);
    },
    [expandedCriterionId, toggleCriterion],
  );

  return (
    <ProtocolDagShell
      isExpanded={isGraphExpanded}
      onToggleExpanded={() => setIsGraphExpanded((value) => !value)}
      summary={
        <>
          <span>{filtered.criteria.length} criteria</span>
          <span>{filtered.atoms.length} atoms</span>
          <span>{filtered.gates.length} gates</span>
          <span>{summary.executionCount} execution edges</span>
          <span>{summary.semanticCount} semantic edges</span>
          {expandedCriterionId ? (
            <button
              className="text-xs text-slate-700 underline"
              onClick={() => setExpandedCriterionId(null)}
            >
              Collapse all
            </button>
          ) : null}
        </>
      }
    >
      <div
        className={
          isGraphExpanded
            ? "flex min-h-0 flex-1 flex-col gap-4 lg:flex-row"
            : "flex flex-col gap-4 lg:flex-row"
        }
      >
        <div
          className={
            isGraphExpanded
              ? "relative min-h-0 min-w-0 flex-1 overflow-hidden rounded-lg border border-slate-200 bg-slate-50"
              : "relative h-[560px] min-w-0 flex-1 overflow-hidden rounded-lg border border-slate-200 bg-slate-50"
          }
        >
          <ReactFlow
            className="h-full w-full"
            style={{ width: "100%", height: "100%" }}
            nodes={nodes}
            edges={edges}
            nodeTypes={nodeTypes}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            nodesDraggable
            nodesConnectable={!readOnly && !busy}
            elementsSelectable
            fitView
            proOptions={{ hideAttribution: true }}
            onNodeClick={(_, node) => {
              setSelectedNode(node.data);
              setSelectedEdge(null);
              setPendingConnection(null);
            }}
            onNodeDoubleClick={(_, node) => {
              if (node.data.kind === "criterion") {
                handleToggleCriterion(node.data.criterionId);
              }
            }}
            onEdgeClick={(_, edge) => {
              setSelectedEdge(edge.data ?? null);
              setSelectedNode(null);
              setPendingConnection(null);
            }}
            onPaneClick={clearSelection}
          >
            <Background gap={18} size={1} color="#cbd5e1" />
            <Controls />
            <MiniMap
              pannable
              zoomable
              nodeStrokeColor="#0f172a"
              nodeColor={(node) => {
                const kind = (node.data as ProtocolDagNodeData | undefined)
                  ?.kind;
                if (kind === "gate") return "#c7d2fe";
                if (kind === "atom") return "#bae6fd";
                return "#e2e8f0";
              }}
            />
          </ReactFlow>
        </div>
        <div className={isGraphExpanded ? "shrink-0 lg:w-80" : "lg:w-80"}>
          <ProtocolDagInspector
            selectedNode={selectedNode}
            selectedEdge={selectedEdge}
            pendingConnection={pendingConnection}
            criteria={filtered.criteria}
            atoms={filtered.atoms}
            gates={filtered.gates}
            connections={filtered.connections}
            readOnly={readOnly}
            busy={busy}
            onExpandCriterion={handleToggleCriterion}
            onChooseConnectionType={(connectionType) => {
              if (!pendingConnection) return;
              commitConnection(
                pendingConnection.fromNodeId,
                pendingConnection.fromNodeType,
                pendingConnection.toNodeId,
                pendingConnection.toNodeType,
                connectionType,
              );
            }}
            onCancelPending={() => setPendingConnection(null)}
            onRemoveConnection={(connectionId) =>
              onRemoveConnection?.(connectionId)
            }
            onClearSelection={clearSelection}
          />
        </div>
      </div>
    </ProtocolDagShell>
  );
}
